// src/context/AudioProvider.js
import { isEmptyObject } from 'jquery';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import love_and_light_image from '../assets/frontend/images/love_&_light.png';

// utility to persist small state in sessionStorage
const STORAGE_KEY = 'romergence_audio_state';
const LIKE_KEY = 'romergence_liked_tracks';

const AudioContext = createContext();

export const useAudio = () => useContext(AudioContext);

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const actionLockRef = useRef(false); // prevents double triggers
  const unlockTimerRef = useRef(null);

  const [tracklist, setTracklist] = useState(null); // full album/playlist object { title, type, tracks: [...] }
  const [currentIndex, setCurrentIndex] = useState(null); // index in tracklist.tracks
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [shuffledOrder, setShuffledOrder] = useState([]);
  const [shuffledPos, setShuffledPos] = useState(0);
  const [likedMap, setLikedMap] = useState(() => {
    try {
      const stored = localStorage.getItem(LIKE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });


  // rehydrate small state
  useEffect(() => {
    try {
      const raw_storage = sessionStorage.getItem(STORAGE_KEY);
      if (raw_storage) {
        const parsed = JSON.parse(raw_storage);
        // only restore flags and ids; do not blindly restore audio.src (we'll load tracklist if matches)
        if (parsed.shuffle) setShuffle(parsed.shuffle);
        if (parsed.tracklist_meta) {
          // we store meta (title_private & type) so album page or API can call loadTracklist again when available
          // it is application-specific — we'll not auto-fetch here.
        }
        if (typeof parsed.currentIndex === 'number') setCurrentIndex(parsed.currentIndex);

      }
      const raw_liked = sessionStorage.getItem(LIKE_KEY);
      if (raw_liked) {
        setLikedMap(loadLikedMap());
      }
    } catch (e) {
      console.warn('AudioProvider: rehydrate failed', e);
    }
  }, []);

  // persist small state
  useEffect(() => {
    const toSave = {
      shuffle,
      currentIndex,
      tracklist_meta: tracklist ? { title_private: tracklist.title_private, type: tracklist.type, title: tracklist.title } : null,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  }, [shuffle, currentIndex, tracklist]);

  // ended event
  useEffect(() => {
    if (!audioRef.current) return;
    const audio = audioRef.current;
    const handleEnded = () => {
      setTimeout(() => {
        unlockAction();
        playNext();
      }, 50);
    };
    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [tracklist, shuffle, shuffledOrder, shuffledPos, currentIndex]);

  // ---- small helpers for action locking ----
  const lockAction = (timeout = 600) => {
    actionLockRef.current = true;
    if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
    unlockTimerRef.current = setTimeout(() => {
      actionLockRef.current = false;
    }, timeout);
  };
  const unlockAction = () => {
    actionLockRef.current = false;
    if (unlockTimerRef.current) { clearTimeout(unlockTimerRef.current); unlockTimerRef.current = null; }
  };

  // Load liked-map from LS -> { "AlbumTitle::TrackTitle": true }
  const loadLikedMap = () => {
    try {
      return JSON.parse(localStorage.getItem(LIKE_KEY)) || {};
    } catch {
      return {};
    }
  };

  const saveLikedMap = (map) => {
    localStorage.setItem(LIKE_KEY, JSON.stringify(map));
  };

  const toggleLikeTrack = (track, tracklist) => {
    if (!track || !tracklist) return;

    const key = `${tracklist.title}::${track.title}::${track.url}`;

    setLikedMap(prev => {
      const updated = { ...prev };

      if (updated[key]) {
        delete updated[key];
      } else {
        updated[key] = true;
      }

      saveLikedMap(updated);
      return updated;
    });
  };

  //  NEW APPROACH
  const toggleLikeTrack2 = (track, tracklist) => {
    if (!track || !tracklist) return;

    var likedTracks = JSON.parse(localStorage.getItem(LIKE_KEY)) || [];

    if (!likedTracks || !likedTracks.tracks) {
      likedTracks = {
        title: 'Liked Songs & Demos',
        type: 'liked', // Optional
        image_url: love_and_light_image,
        description: 'Placeholder for the description.',
        tracks: []
      };
    }

    const likedTracksArray = Array.isArray(likedTracks.tracks)
      ? likedTracks.tracks
      : [];

    // Check exists
    const exists = likedTracksArray.some(
      item => item.random_id === track.random_id
    );

    // REMOVE TRACK
    if (exists) {

      const updatedLikedTracks = {
        type: likedTracks.type,
        title: likedTracks.title || "Liked Songs & Demos",
        image_url: likedTracks.image_url,
        description: likedTracks.description || 'Placeholder for the description.',
        tracks: likedTracks.tracks.filter(
          item => item.random_id !== track.random_id
        ),
      };

      // Save LS + memory
      localStorage.setItem(LIKE_KEY, JSON.stringify(updatedLikedTracks));
      setLikedMap(updatedLikedTracks);

      // Special behavior when listening inside "Liked Songs"
      if (tracklist.type === "liked") {

        const oldIndex = currentIndex;
        const newLength = updatedLikedTracks.tracks.length;

        // Update the tracklist in UI
        setTracklist(updatedLikedTracks);

        // Case 1: nothing left → stop playback
        if (newLength === 0) {
          setIsPlaying(false);
          setIsPaused(false);
          setCurrentIndex(null);
          audioRef.current.pause();
          audioRef.current.src = "";
          return;
        }

        // Case 2: removed last track → move to last valid
        if (oldIndex >= newLength) {
          setCurrentIndex(newLength - 1);
          if (isPlaying) {
            playTrack(newLength - 1, updatedLikedTracks);  // <-- pass updated TL
          }
          return;
        }

        // Case 3: removed current track → play next one
        setCurrentIndex(oldIndex);

        if (isPlaying) {
          playTrack(oldIndex, updatedLikedTracks);  // <-- pass updated TL
        }

      }

      return;
    }


    // ADD TRACK
    if (tracklist.type === 'album' || tracklist.type === 'demos') {
      const newTrack = {
        ...track,
        tracklistTitle: tracklist.title,
        tracklistImageURL: tracklist.image_url,
        tracklistRandomID: tracklist.random_id
      };

      const updated = {
        ...likedTracks,
        tracks: [...likedTracks.tracks, newTrack]
      };

      localStorage.setItem(LIKE_KEY, JSON.stringify(updated));
      setLikedMap(updated);  // <-- CORRECT

      return;
    }

    var cr = 0;
  };

  // playable check
  const isPlayable = (idx, list = tracklist) => {
    if (!list || !list.tracks) return false;
    const t = list.tracks[idx];
    return t && t.status === 'active' && t.url;
  };

  const loadTracklist = (newTracklist, options = { startIndex: 0, autoPlay: false }) => {
    // reset shuffle
    setShuffledOrder([]);
    setShuffledPos(0);
    setTracklist(newTracklist);

    if (options && typeof options.startIndex === 'number') {
      setCurrentIndex(options.startIndex);
      if (options.autoPlay && isPlayable(options.startIndex, newTracklist)) {
        // set src & play
        requestAnimationFrame(() => {
          const audio = audioRef.current;
          if (audio) {
            audio.src = newTracklist.tracks[options.startIndex].url;
            audio.currentTime = 0;
            audio.play().then(() => {
              setIsPlaying(true);
              setIsPaused(false);
              unlockAction();
            }).catch(() => {
              // autoplay blocked
              setIsPlaying(false);
              setIsPaused(true);
              unlockAction();
            });
          } else {
            unlockAction();
          }
        });
      }
    }
  };
  const clearTracklist = () => {
    pause();

    setTracklist(null);
    setCurrentIndex(null);
    setIsPlaying(false);
    setIsPaused(false);
    setShuffledOrder([]);
    setShuffledPos(0);
  };

  const playTrack = (index, newTracklist = null, options = {}) => {
    if (!options.skipLock) {
      if (actionLockRef.current) return;
      lockAction(800);
    }

    // if new tracklist object passed and differs, load it
    if (newTracklist && newTracklist.id !== tracklist?.id) {
      setTracklist(newTracklist);
      // allow continuing: we'll still try to play index from newTracklist below
    } else if (!tracklist || !tracklist.tracks) {
      unlockAction();
      return;
    }

    if (!isPlayable(index)) {
      unlockAction();
      return;
    }

    const goldSoundBars = document.querySelector('.colored-sound-bars');
    const whiteSoundBars = document.querySelector('.white-sound-bars');

    setCurrentIndex(index);
    const audio = audioRef.current;
    if (!audio) {
      unlockAction();
      return;
    }
    const t = (newTracklist || tracklist).tracks[index];
    if (!t || !t.url) {
      unlockAction();
      return;
    }

    if (audio.src !== t.url) audio.src = t.url;
    audio.currentTime = 0;

    audio.play().then(() => {
      setIsPlaying(true);
      setIsPaused(false);
      unlockAction();

      // remove paused class
      if (goldSoundBars) goldSoundBars.classList.remove('paused');
      if (whiteSoundBars) whiteSoundBars.classList.remove('paused');
    }).catch(() => {
      // play failed (autoplay blocked or other)
      setIsPlaying(false);
      setIsPaused(true);
      // unlock after a small delay so UI not permanently locked
      setTimeout(() => unlockAction(), 600);
    });
  };

  const pause = () => {
    if (actionLockRef.current) { /* allow pause to still run */ }

    const audio = audioRef.current;
    if (!audio) return;

    const goldSoundBars = document.querySelector('.colored-sound-bars');
    const whiteSoundBars = document.querySelector('.white-sound-bars');

    audio.pause();
    setIsPaused(true);
    setIsPlaying(false);
    // pause the visualizer animation
    if (goldSoundBars) goldSoundBars.classList.add('paused');
    if (whiteSoundBars) whiteSoundBars.classList.add('paused');
  };

  const resume = () => {
    if (actionLockRef.current) return;
    lockAction(800);

    const audio = audioRef.current;
    if (!audio) { unlockAction(); return; }

    const goldSoundBars = document.querySelector('.colored-sound-bars');
    const whiteSoundBars = document.querySelector('.white-sound-bars');

    audio.play().then(() => {
      setIsPlaying(true);
      setIsPaused(false);
      unlockAction();
      // remove paused class
      if (goldSoundBars) goldSoundBars.classList.remove('paused');
      if (whiteSoundBars) whiteSoundBars.classList.remove('paused');
    }).catch(() => {
      setIsPlaying(false);
      setIsPaused(true);
      setTimeout(() => unlockAction(), 600);
    });
  };

  // GENERATE shuffle order deterministically
  const generateShuffledOrder = (seedIndex = null) => {
    if (!tracklist) return [];
    const indices = tracklist.tracks.map((_, i) => i);
    // simple Fisher-Yates
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    // if seedIndex is provided, rotate the order so that current index is the first element
    if (typeof seedIndex === 'number') {
      const pos = indices.indexOf(seedIndex);
      if (pos > 0) {
        // rotate so that seedIndex sits at position 0
        const rotated = [...indices.slice(pos), ...indices.slice(0, pos)];
        setShuffledOrder(rotated);
        setShuffledPos(1); // next will pick rotated[1]
        return rotated;
      }
    }
    setShuffledOrder(indices);
    setShuffledPos(0); // next will pick indices[0]
    return indices;
  };

  // toggleShuffle: deterministic behavior
  const toggleShuffle = () => {

    console.log("toggleShuffle(start) shuffle:", shuffle, "currentIndex:", currentIndex, "shuffledOrder:", shuffledOrder);

    if (actionLockRef.current) return;
    lockAction(500);

    setShuffle(prev => {
      const newShuffle = !prev;
      if (newShuffle) {
        // enable shuffle: create new order that preserves currentIndex position
        const seed = (typeof currentIndex === 'number') ? currentIndex : null;
        //const order = generateShuffledOrder(seed);
        const order = generateShuffledOrder();

        // IMPORTANT: Immediately set the first shuffled index as the current track
        if (order && order.length > 0) {
          // If currentIndex=null, OR seed=null → pick first shuffled track
          const newCurrent =
            (typeof currentIndex === "number")
              ? currentIndex                 // keep real position
              : order[0];                    // use first shuffle entry

          setCurrentIndex(newCurrent);
          setShuffledOrder(order);
          setShuffledPos(1); // next track = order[1]
        }
      } else {
        // disable shuffle
        setShuffledOrder([]);
        setShuffledPos(0);

        // OPTIONAL: When turning shuffle off, return to the track’s true position
        if (currentIndex != null) {
          setCurrentIndex(currentIndex);
        }
      }
      // unlock after tiny delay to allow shuffle computation
      setTimeout(() => unlockAction(), 400);
      console.log("toggleShuffle(end) shuffle:", shuffle, "currentIndex:", currentIndex, "shuffledOrder:", shuffledOrder);
      return newShuffle;
    });
  };

  // playNext deterministic
  const playNext = () => {
    if (actionLockRef.current) return;
    lockAction(500);

    if (!tracklist || !tracklist.tracks || tracklist.tracks.length === 0) {
      unlockAction();
      return;
    }

    if (shuffle) {
      // ensure we have an order
      let order = shuffledOrder.length ? shuffledOrder : generateShuffledOrder(currentIndex);

      // Determine shuffledPos if first play or mismatched
      let pos = shuffledPos;
      if (pos === 0 && currentIndex != null) {
        const currentPos = order.indexOf(currentIndex);
        pos = (currentPos + 1) % order.length;
        setShuffledPos(pos);
      }

      const nextIdx = order[pos];
      // advance shuffledPos deterministically
      setShuffledPos(p => (p + 1) % order.length);
      // play nextIdx if playable; otherwise find next playable in order
      if (isPlayable(nextIdx)) {
        playTrack(nextIdx, null, { skipLock: true });
      } else {
        const n = order.find(i => isPlayable(i));
        if (typeof n === 'number') playTrack(n);
        else {
          pause();
          unlockAction();
        }
      }
    } else {
      // normal next with wrap and skipping inactive
      const len = tracklist.tracks.length;
      const start = (currentIndex === null ? 0 : (currentIndex + 1) % len);
      for (let i = 0; i < len; i++) {
        const idx = (start + i) % len;
        if (isPlayable(idx)) {
          playTrack(idx, null, { skipLock: true });
          return;
        }
      }
      pause();
      unlockAction();
    }
  };

  // playPrev deterministic
  const playPrev = () => {
    if (actionLockRef.current) return;
    lockAction(500);

    if (!tracklist || !tracklist.tracks || tracklist.tracks.length === 0) {
      unlockAction();
      return;
    }

    if (shuffle) {
      // step back in shuffledOrder
      const order = shuffledOrder.length ? shuffledOrder : generateShuffledOrder(currentIndex);
      // current shuffledPos points to next position (we incremented after calls). To go prev, step back by 2 then +1 to keep parity with playNext logic
      let newPos = shuffledPos - 2;
      if (newPos < 0) newPos = (order.length + (newPos % order.length)) % order.length;
      const idx = order[newPos];
      setShuffledPos(p => (newPos + 1) % order.length);
      if (isPlayable(idx)) {
        playTrack(idx, null, { skipLock: true });
      } else {
        const n = order.find(i => isPlayable(i));
        if (typeof n === 'number') playTrack(n);
        else {
          pause();
          unlockAction();
        }
      }
    } else {
      const len = tracklist.tracks.length;
      // start from previous (wrap)
      let start = (currentIndex === null ? 0 : (currentIndex - 1 + len) % len);
      for (let i = 0; i < len; i++) {
        const idx = (start - i + len) % len;
        if (isPlayable(idx)) {
          playTrack(idx, null, { skipLock: true });
          return;
        }
      }
      pause();
      unlockAction();
    }
  };

  // stubs for removeFromPlaylist left unchanged (you had them)
  const removeFromPlaylist = async (playlistId, indexToRemove) => { /* ... */ };

  // remove-from-playlist behaviour:
  const removeFromPlaylistOriginal = async (playlistId, indexToRemove) => {
    if (!tracklist || tracklist.type !== 'playlist') return;
    // optimistic local removal
    const newTracks = [...tracklist.tracks];
    const removed = newTracks.splice(indexToRemove, 1)[0];
    const oldIndex = currentIndex;
    setTracklist({ ...tracklist, tracks: newTracks });
    // if removed the currently playing track:
    if (oldIndex === indexToRemove) {
      // try to play next active
      const nextCandidate = newTracks.findIndex(t => t.status === 'active');
      if (nextCandidate >= 0) {
        playTrack(nextCandidate);
      } else {
        pause();
        setCurrentIndex(null);
      }
    } else if (oldIndex > indexToRemove) {
      // shift current index left by 1
      setCurrentIndex(oldIndex - 1);
    }
    // call actual API to update playlist; handle failures (rollback)
  };

  // cleanup timers on unmount
  useEffect(() => () => {
    if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
  }, []);

  return (
    <AudioContext.Provider value={{
      audioRef,
      tracklist,
      currentIndex,
      isPlaying,
      isPaused,
      shuffle,
      shuffledOrder,
      loadTracklist,
      clearTracklist,
      playTrack,
      pause,
      resume,
      playNext,
      playPrev,
      likedMap,
      toggleLikeTrack,
      toggleLikeTrack2,
      generateShuffledOrder,
      toggleShuffle,
      removeFromPlaylist,
      setTracklist,
    }}>
      {children}
    </AudioContext.Provider>
  );
};
